import Equalizer from "ui/templates/Game/MixPuzzle/MixPuzzle/atoms/Equalizer/Equalizer";
import PanKnob from "ui/templates/Game/MixPuzzle/MixPuzzle/atoms/PanKnob/PanKnob";
import { MixPuzzleInstrument } from "shared/games/mixpuzzles/types";
import Button from "ui/atoms/Button/Button";
import Instrument from "shared/games/instruments/Instrument";
import Panel from "./MixConsolePanel";
import prettyInstrument from "util/formatters/prettyInstrument";
import { difficultySettings } from "../../constants";
import { State } from "../../state/reducer";
import classNames from "classnames";
import MixPuzzleVolumeSlider from "../../atoms/MixPuzzleVolumeSlider";

interface Props {
  state: State;
  instrument: MixPuzzleInstrument;
  onRequestPlaybackChange: (
    instrument: Instrument,
    params: Partial<MixPuzzleInstrument>
  ) => void;
}

export default function MixConsole({
  instrument,
  onRequestPlaybackChange,
  state,
}: Props) {
  const compressionEnabled = difficultySettings.compression(state) === true;

  return (
    <div className="rounded bg-slate-300 p-1">
      {/* desktop view */}
      <div className="hidden md:block">
        <div className="grid gap-1">
          <div className={"rounded bg-black px-2 py-1 text-center font-brik"}>
            Patched in:{" "}
            <span className={"text-purple-400"}>
              {prettyInstrument(instrument.instrument)}
            </span>
          </div>

          <div className="relative h-72">
            <Equalizer
              value={instrument}
              onChange={(params) => {
                onRequestPlaybackChange(instrument.instrument, params);
              }}
            />
          </div>

          {compressionEnabled && (
            <Panel
              title={"Compression"}
              orientation={"vertical"}
              bodyClassName="grid gap-2 grid-cols-2 p-2 pb-1"
            >
              <Button
                theme="default"
                block
                active={instrument.comp === null}
                onClick={() =>
                  onRequestPlaybackChange(instrument.instrument, { comp: null })
                }
              >
                Off
              </Button>

              <Button
                theme="default"
                block
                active={instrument.comp === 7}
                onClick={() =>
                  onRequestPlaybackChange(instrument.instrument, { comp: 7 })
                }
              >
                7 DB
              </Button>
            </Panel>
          )}
        </div>
      </div>

      {/* mobile view */}
      <div className="block md:hidden">
        <div className="grid gap-1">
          <div className="grid grid-cols-[1fr,100px] gap-1">
            <div
              className={classNames(
                "relative",
                compressionEnabled ? "h-auto" : "h-48"
              )}
            >
              <Equalizer
                value={instrument}
                onChange={(params) => {
                  onRequestPlaybackChange(instrument.instrument, params);
                }}
              />
            </div>

            <div className="grid grid-cols-1 gap-1">
              <Panel orientation="vertical" title="Pan" bodyClassName="p-2">
                <PanKnob
                  value={instrument.pan}
                  onChange={(pan) =>
                    onRequestPlaybackChange(instrument.instrument, { pan })
                  }
                />
              </Panel>

              {compressionEnabled && (
                <Panel
                  title={"Comp"}
                  orientation={"vertical"}
                  bodyClassName="grid gap-1 p-2 pb-1"
                >
                  <Button
                    theme="default"
                    block
                    active={instrument.comp === null}
                    onClick={() =>
                      onRequestPlaybackChange(instrument.instrument, {
                        comp: null,
                      })
                    }
                  >
                    Off
                  </Button>

                  <Button
                    theme="default"
                    block
                    active={instrument.comp === 7}
                    onClick={() =>
                      onRequestPlaybackChange(instrument.instrument, {
                        comp: 7,
                      })
                    }
                  >
                    7 DB
                  </Button>
                </Panel>
              )}
            </div>
          </div>

          <Panel title="Volume" bodyClassName="p-2" orientation="horizontal">
            <MixPuzzleVolumeSlider
              value={instrument.volume}
              onChange={(volume) =>
                onRequestPlaybackChange(instrument.instrument, { volume })
              }
            />
          </Panel>
        </div>
      </div>
    </div>
  );
}
